import type { ConceptId } from "../data/concepts";
import type { FigureId } from "../data/figures";
import type { GateId } from "./types";

/**
 * The eight authored question types. Each exists because it exercises a
 * distinct reading skill; a bank that is all `comprehension` would still be
 * a bank of recognition questions.
 */
export type QuestionType =
  | "comprehension" // 原文理解 — what does this passage actually say?
  | "interpretation" // 語境解讀 — why is it said here, to this person?
  | "concept" // 核心概念 — what does 仁/禮/義 mean across the book?
  | "linkage" // 章句關聯 — how do two passages bear on each other?
  | "conflict" // 觀點衝突 — passages that pull against each other
  | "figure" // 人物弟子 — who is speaking, and why does that change the answer?
  | "application" // 現實應用 — take it into a situation the student is actually in
  | "misread"; // 誤解識別 — the plausible wrong reading, named and refuted

/**
 * Cognitive demand, Bloom-style. `remember` is deliberately absent: pure
 * recall items are what this rebuild removed.
 */
export type Cognitive = "understand" | "apply" | "analyze" | "evaluate";

/** 1 入門 · 2 進階 · 3 精研 */
export type Difficulty = 1 | 2 | 3;

export interface BankOption {
  id: string;
  text: string;
  /**
   * Why this option is not the answer — shown after answering. Required on
   * every non-answer option by the quality standard: a wrong choice the
   * student cannot diagnose teaches nothing. In a 誤解識別 item phrased
   * "which of these is the misreading", this explains why the others are
   * sound readings rather than why they are wrong.
   */
  why?: string;
}

export interface BankItem {
  review?: { version: string; sha256: string; gate: GateId; sourceCorpusSha256: string };
  id: string;
  type: QuestionType;
  /** Analect ids this item is anchored to. First is the primary. */
  refs: number[];
  concepts: ConceptId[];
  figures?: FigureId[];
  difficulty: Difficulty;
  cognitive: Cognitive;
  /** 學習目的 — the one thing this item is meant to teach. */
  purpose: string;
  stem: string;
  /** Scenario or supplied passage rendered above the options. */
  context?: string;
  options: BankOption[];
  answerId: string;
  /** Why the right answer is right — always more than a restatement. */
  explanation: string;
}

/**
 * Type → gate. The four gates are the student-facing progression, and each
 * is fed by two or more question types so no gate degenerates into a single
 * mechanical drill.
 *
 *   通文 meaning — read it right
 *   辨惑 misread — don't be led off by a plausible wrong reading
 *   致用 context — take it into a real situation
 *   通貫 recall  — connect it across the book
 *
 * The GateId strings are unchanged from the previous release on purpose:
 * saved progress, the leaderboard payload, and the User Center learning
 * evidence resourceKey (`ly:qNNNN:<gate>`) are all keyed on them.
 */
export const TYPE_GATE: Record<QuestionType, GateId> = {
  comprehension: "meaning",
  interpretation: "meaning",
  misread: "misread",
  conflict: "misread",
  application: "context",
  concept: "recall",
  linkage: "recall",
  figure: "recall",
};

export const TYPE_LABEL: Record<QuestionType, string> = {
  comprehension: "原文理解",
  interpretation: "語境解讀",
  concept: "核心概念",
  linkage: "章句關聯",
  conflict: "觀點衝突",
  figure: "人物弟子",
  application: "現實應用",
  misread: "誤解識別",
};

export const COGNITIVE_LABEL: Record<Cognitive, string> = {
  understand: "理解",
  apply: "運用",
  analyze: "分析",
  evaluate: "評價",
};

export const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  1: "入門",
  2: "進階",
  3: "精研",
};

/** Item-level editorial routing does not remap historical gate records. */
export function gateForBankItem(item: BankItem): GateId { return item.review?.gate ?? TYPE_GATE[item.type]; }
